import { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { getFileExtension } from './fileUpload.utils'
import { getFileTypeColor } from './fileUpload.constants'
import { DocGlyph } from './fileUpload.glyphs'

export type UploadCardVariant = 'staging' | 'staged' | 'uploading' | 'success' | 'error' | 'invalid'

export interface UploadStatusCardProps {
  variant: UploadCardVariant
  fileName: string
  /** For 'staged' pass formatStagedMeta(...) — row count + size. */
  fileSizeText?: string
  /** 0..100, only for the 'staging' variant. */
  progress?: number
  errorMessage?: string | null
  /** Renders the remove (X) action on the invalid / staged file row. */
  onRemove?: () => void
  /** Object-URL of an image file: swaps the format badge for a clickable thumbnail. */
  previewUrl?: string | null
}

const CheckGlyph = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.6'
       strokeLinecap='round' strokeLinejoin='round' aria-hidden>
    <polyline points='20 6 9 17 4 12' />
  </svg>
)

const CrossGlyph = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.6' strokeLinecap='round'
       aria-hidden>
    <path d='M18 6 6 18' />
    <path d='M6 6l12 12' />
  </svg>
)

const FileRow = ({
  variant,
  fileName,
  fileSizeText,
  progress,
  errorMessage,
  onRemove,
  previewUrl,
}: UploadStatusCardProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const extension = getFileExtension(fileName)
  const isBad = variant === 'invalid' || variant === 'error'
  const isDone = variant === 'success' || variant === 'staged'
  return (
    <>
      <Row $tone={isBad ? 'bad' : isDone ? 'done' : 'plain'}>
        {previewUrl ? (
          <Thumb
            type='button'
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'סגירת התצוגה המוגדלת' : 'הגדלת התמונה'}
          >
            <ThumbImage src={previewUrl} alt={fileName} />
          </Thumb>
        ) : (
          <FileBadge $color={isBad ? undefined : getFileTypeColor(extension)} $bad={isBad}>
            {extension ? extension.slice(1, 5).toUpperCase() : '?'}
          </FileBadge>
        )}
        <FileMeta>
          <FileName>{fileName}</FileName>
          {isBad && errorMessage ? (
            <FileSub $bad>{errorMessage}</FileSub>
          ) : (
            fileSizeText && <FileSub>{fileSizeText}</FileSub>
          )}
          {variant === 'staging' && (
            <RowBar
              role='progressbar'
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress ?? 0)}
            >
              <RowBarFill style={{ width: `${progress ?? 0}%` }} />
            </RowBar>
          )}
        </FileMeta>
        <RowActions>
          {variant === 'staging' && <RowPct aria-hidden>{`${Math.round(progress ?? 0)}%`}</RowPct>}
          {isDone && (
            <StatusDot $tone='good'>
              <CheckGlyph />
            </StatusDot>
          )}
          {isBad && (
            <StatusDot $tone='bad'>
              <CrossGlyph />
            </StatusDot>
          )}
          {onRemove && (
            <RemoveButton type='button' onClick={onRemove} aria-label='הסרת הקובץ'>
              <CrossGlyph />
            </RemoveButton>
          )}
        </RowActions>
      </Row>
      {previewUrl && isExpanded && (
        <PreviewBox onClick={() => setIsExpanded(false)}>
          <PreviewImage src={previewUrl} alt={fileName} />
        </PreviewBox>
      )}
    </>
  )
}

const PROGRESS_TICK_MS = 300
// The real upload progress is unknown — the readout eases toward a cap and
// the phase switches away on the actual server response.
const PROGRESS_CAP = 92

const useSimulatedProgress = (isActive: boolean): number => {
  const [progress, setProgress] = useState<number>(0)

  useEffect(() => {
    if (!isActive) return
    setProgress(0)
    const timer = setInterval(() => {
      setProgress((prev) => Math.min(PROGRESS_CAP, prev + Math.random() * 14 + 6))
    }, PROGRESS_TICK_MS)
    return () => clearInterval(timer)
  }, [isActive])

  return progress
}

/**
 * Upload phase card: staging / staged file rows with a local progress bar,
 * animated sending state with a simulated progress readout, popped success /
 * failure badges with a drawn glyph, and the invalid-file row.
 * Purely presentational.
 */
const UploadStatusCard = (props: UploadStatusCardProps) => {
  const { variant, errorMessage } = props
  const progress = useSimulatedProgress(variant === 'uploading')

  if (variant === 'invalid' || variant === 'staging' || variant === 'staged') {
    return <FileRow {...props} />
  }

  if (variant === 'uploading') {
    return (
      <Phase>
        <Art>
          <Ring />
          <RingSlow />
          <Doc>
            <DocGlyph />
          </Doc>
        </Art>
        <PhaseTitle>שולחים לשרת</PhaseTitle>
        <PhaseSub>אל תסגרו את החלון — הקובץ בבדיקה ובטעינה.</PhaseSub>
        <ProgressTrack
          role='progressbar'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <ProgressFill style={{ width: `${progress}%` }} />
        </ProgressTrack>
        <ProgressReadout aria-hidden>{`${Math.round(progress)}%`}</ProgressReadout>
        {props.fileName && <FileRow {...props} />}
      </Phase>
    )
  }

  if (variant === 'success') {
    return (
      <Phase>
        <Art>
          <ResultBadge $tone='good'>
            <CheckGlyph />
          </ResultBadge>
        </Art>
        <PhaseTitle>ההעלאה הושלמה</PhaseTitle>
        <PhaseSub>הקובץ התקבל והנתונים עודכנו בהצלחה.</PhaseSub>
        {props.fileName && <FileRow {...props} />}
      </Phase>
    )
  }

  return (
    <Phase>
      <Art>
        <ResultBadge $tone='bad'>
          <CrossGlyph />
        </ResultBadge>
      </Art>
      <PhaseTitle>ההעלאה נכשלה</PhaseTitle>
      <PhaseSub>הקובץ הגיע לשרת אך העיבוד נכשל. הנתונים לא השתנו.</PhaseSub>
      {errorMessage && (
        <ErrorBox>
          <ErrorTitle>פרטי השגיאה</ErrorTitle>
          <ErrorDetail>{errorMessage}</ErrorDetail>
        </ErrorBox>
      )}
    </Phase>
  )
}

export default UploadStatusCard

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const lift = keyframes`
  0%, 100% { transform: translateY(3px); opacity: 0.75; }
  50% { transform: translateY(-3px); opacity: 1; }
`

const pop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.06); }
  100% { transform: scale(1); opacity: 1; }
`

const draw = keyframes`
  to { stroke-dashoffset: 0; }
`

const rise = keyframes`
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
`

const Phase = styled.div`
  direction: rtl;
  padding: 2rem 1.25rem 1.25rem;
  text-align: center;
`

const Art = styled.div`
  width: 6rem;
  height: 6rem;
  margin: 0 auto 1.375rem;
  position: relative;
  display: grid;
  place-items: center;
`

const Ring = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.colors.line2};
  border-top-color: ${({ theme }) => theme.colors.accentDeep};
  animation: ${spin} 900ms linear infinite;
`

const RingSlow = styled.span`
  position: absolute;
  inset: 0.625rem;
  border-radius: 50%;
  border: 2.5px solid transparent;
  border-inline-start-color: ${({ theme }) => theme.colors.accent};
  animation: ${spin} 1500ms linear infinite reverse;
`

const Doc = styled.span`
  width: 2.125rem;
  height: 2.125rem;
  color: ${({ theme }) => theme.colors.icon};
  animation: ${lift} 1400ms ease-in-out infinite;

  svg {
    width: 100%;
    height: 100%;
  }
`

const ResultBadge = styled.span<{ $tone: 'good' | 'bad' }>`
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 50%;
  display: grid;
  place-items: center;
  animation: ${pop} 420ms ease both;
  background: ${({ $tone, theme }) =>
    $tone === 'good' ? theme.colors.accentSoft : theme.colors.dangerSoft};
  border: 2px solid ${({ $tone, theme }) =>
    $tone === 'good' ? theme.colors.accentDeep : theme.colors.danger};
  color: ${({ $tone, theme }) =>
    $tone === 'good' ? theme.colors.success : theme.colors.danger};

  svg {
    width: 2.625rem;
    height: 2.625rem;
  }

  svg path,
  svg polyline {
    stroke-dasharray: 56;
    stroke-dashoffset: 56;
    animation: ${draw} 460ms ease 240ms forwards;
  }

  svg path:nth-child(2) {
    animation-delay: 380ms;
  }
`

const PhaseTitle = styled.h4`
  margin: 0 0 0.375rem;
  font-size: ${({ theme }) => theme.text.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  animation: ${rise} 380ms ease 160ms both;
`

const PhaseSub = styled.div`
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink3};
  max-width: 38ch;
  margin: 0 auto;
  animation: ${rise} 380ms ease 240ms both;
`

const ProgressTrack = styled.div`
  max-width: 17.5rem;
  margin: 1.25rem auto 0;
  height: 0.3125rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.line2};
  overflow: hidden;
`

const ProgressFill = styled.span`
  display: block;
  height: 100%;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.accentDeep};
  transition: width ${({ theme }) => theme.motion.base} linear;
`

const ProgressReadout = styled.div`
  margin-top: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  color: ${({ theme }) => theme.colors.ink4};
`

const ErrorBox = styled.div`
  margin-top: 1.125rem;
  padding: 0.75rem 0.875rem;
  background: ${({ theme }) => theme.colors.dangerSoft};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.md};
  text-align: start;
  animation: ${rise} 380ms ease 300ms both;
`

const ErrorTitle = styled.div`
  font-size: ${({ theme }) => theme.text.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.danger};
  margin-bottom: 0.1875rem;
`

const ErrorDetail = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  color: ${({ theme }) => theme.colors.ink2};
  line-height: 1.5;
`

const Row = styled.div<{ $tone: 'plain' | 'done' | 'bad' }>`
  direction: rtl;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6875rem 0.75rem;
  margin-top: 0.875rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.surface};
  text-align: start;
  animation: ${rise} 380ms ease 320ms both;

  ${({ $tone, theme }) =>
    $tone === 'done' &&
    `
    background: ${theme.colors.successSoft};
  `}

  ${({ $tone, theme }) =>
    $tone === 'bad' &&
    `
    background: ${theme.colors.dangerSoft};
  `}
`

const FileBadge = styled.span<{ $color?: string; $bad: boolean }>`
  width: 2.125rem;
  height: 2.125rem;
  border-radius: ${({ theme }) => theme.radii.sm};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.surface};
  background: ${({ $bad, $color, theme }) => ($bad ? theme.colors.danger : $color)};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
`

const Thumb = styled.button`
  width: 2.125rem;
  height: 2.125rem;
  padding: 0;
  flex-shrink: 0;
  overflow: hidden;
  cursor: zoom-in;
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.bgLayout};
  transition: border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`

const PreviewBox = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  cursor: zoom-out;
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.bgLayout};
`

const PreviewImage = styled.img`
  width: 100%;
  max-height: 18rem;
  display: block;
  object-fit: contain;
`

const FileMeta = styled.span`
  flex: 1;
  min-width: 0;
`

const FileName = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FileSub = styled.span<{ $bad?: boolean }>`
  display: block;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  color: ${({ $bad, theme }) => ($bad ? theme.colors.danger : theme.colors.ink3)};
  margin-top: 0.125rem;
`

const RowActions = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

const RowBar = styled.span`
  display: block;
  height: 0.3125rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.line2};
  overflow: hidden;
  margin-top: 0.5rem;
`

const RowBarFill = styled.span`
  display: block;
  height: 100%;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.accentDeep};
  transition: width ${({ theme }) => theme.motion.fast} linear;
`

const RowPct = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  min-width: 2.375rem;
  text-align: end;
`

const StatusDot = styled.span<{ $tone: 'good' | 'bad' }>`
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ $tone, theme }) =>
    $tone === 'good' ? theme.colors.success : theme.colors.danger};
  color: ${({ theme }) => theme.colors.surface};

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`

const RemoveButton = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.colors.ink4};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 0.9375rem;
    height: 0.9375rem;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink};
  }
`
